import { contact, social, homeFaqItems } from './siteContent';
import { logisticsProduct, fleetProduct } from './products';
import { seoLandingPages } from './seoLandingPages';
import { officeLocations } from './contactPageContent';
import { CANONICAL_SITEMAP_ENTRIES, FLEET_SAAS_HUB, PLAY_STORE_LINKS } from './seoUrlManifest';

export const SITE_NAME = 'FastWhistle';
export const PARENT_COMPANY = '5INCH VENTURES PRIVATE LIMITED';
export const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://fastwhistle.com').replace(/\/$/, '');
export const DEFAULT_OG_IMAGE = `${SITE_URL}/assets/img/fastwhistle.png`;
export const TWITTER_HANDLE = '@fast_whistle';

const OFFICE = officeLocations[0];

const baseKeywords = [
  'FastWhistle',
  'pharma distributor logistics',
  'medicine delivery India',
  'medical delivery service',
  'pharmaceutical distribution software',
  'fleet management pharma',
  'distributor delivery app',
  'pharma logistics Gujarat',
  'medicine delivery Surat',
  'medicine delivery Ahmedabad',
  'medicine delivery Pune',
  'medicine delivery Mumbai',
  'fleet service Surat',
  'best fleet service in Surat',
  'chemist delivery service',
  'pharma parcel delivery',
];

function autoLandingSeo(page) {
  const cityKw = page.city ? [`${page.serviceType === 'logistics' ? 'medicine delivery' : 'pharma fleet'} ${page.city}`] : [];
  const isLogistics = page.serviceType === 'logistics';
  const isFleet = page.serviceType === 'fleet' || page.serviceType === 'fleet-feature';

  return {
    path: page.path,
    title: `${page.h1} | FastWhistle`,
    description: page.answerBlock?.slice(0, 158) || page.subtitle,
    keywords: [...baseKeywords, ...cityKw, ...(page.hub === 'logistics' ? ['B2B medicine delivery', 'pharma logistics'] : ['fleet management software India', 'pharma field force'])],
    faq: page.faq,
    service: page.city
      ? {
          name: page.h1,
          description: page.answerBlock,
          areaServed: [page.city, page.state || 'India', 'India'],
          serviceType: isLogistics ? 'Pharmaceutical delivery service' : 'Fleet management software',
        }
      : isLogistics
        ? {
            name: 'Medical Delivery Service India',
            description: page.answerBlock,
            areaServed: ['Ahmedabad', 'Surat', 'Pune', 'Mumbai', 'India'],
            serviceType: 'Pharmaceutical logistics',
          }
        : null,
    localBusiness: Boolean(page.city && page.serviceType === 'logistics'),
    softwareApplication: isFleet,
    city: page.city,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      page.hub === 'logistics'
        ? { name: 'Logistics', path: '/logistics' }
        : page.hub === 'fleet-saas'
          ? { name: 'Fleet SaaS', path: FLEET_SAAS_HUB }
          : null,
      { name: page.eyebrow, path: page.path },
    ].filter(Boolean),
  };
}

const landingPageSeo = Object.fromEntries(
  seoLandingPages.map((page) => [page.pageKey, autoLandingSeo(page)])
);

export const PAGE_SEO = {
  home: {
    path: '/',
    title: 'FastWhistle | Medicine Delivery & Best Fleet Service for Pharma Distributors',
    description:
      'Medical delivery service in Ahmedabad, Surat, Pune & Mumbai. Best fleet management software for medicine distributors pan-India — same-day pharma logistics, live GPS, attendance & collections.',
    keywords: [
      ...baseKeywords,
      'medicine delivery service',
      'medical delivery service India',
      'best fleet service in Surat',
      'fleet management software India',
      'pharma logistics company',
      'medicine distributor software',
      'ZeroMDS integration',
      'same day pharma delivery',
    ],
    faq: homeFaqItems,
    breadcrumbs: [{ name: 'Home', path: '/' }],
    localBusiness: true,
  },
  logistics: {
    path: '/logistics',
    title: 'Medicine Delivery Service | Ahmedabad, Surat, Pune & Mumbai',
    description:
      'Managed B2B medicine delivery for pharma distributors — same-day parcel delivery in Ahmedabad, Surat, Pune & Mumbai. Per-parcel pricing, live tracking, FastWhistle riders.',
    keywords: [
      ...baseKeywords,
      'medicine delivery service',
      'medical delivery service',
      'same day medicine delivery',
      'pharma parcel delivery Ahmedabad',
      'pharma parcel delivery Surat',
      'managed delivery for distributors',
      'chemist delivery Gujarat',
      'third party pharma logistics',
      'medicine courier service',
      'pharmaceutical delivery service India',
    ],
    faq: logisticsProduct.faq,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Logistics', path: '/logistics' },
    ],
    service: {
      name: 'FastWhistle Managed Pharma Logistics & Medical Delivery',
      description:
        'Same-day medicine and medical parcel delivery service for pharmaceutical distributors in Ahmedabad, Surat, Pune, and Mumbai using FastWhistle riders.',
      areaServed: ['Ahmedabad', 'Surat', 'Pune', 'Mumbai', 'India'],
      serviceType: 'Medical delivery service',
    },
    localBusiness: true,
  },
  fleetSaas: {
    path: FLEET_SAAS_HUB,
    title: 'Best Pharma Fleet Management Software India | FastWhistle Fleet SaaS',
    description:
      'Pan-India pharma fleet SaaS for medicine distributors — live GPS, attendance, TADA, collections & ZeroMDS sync. Headquartered in Surat. Separate from managed delivery in 4 cities.',
    keywords: [
      ...baseKeywords,
      'best fleet service in Surat',
      'fleet management software India',
      'fleet service Surat',
      'pharma salesman app',
      'field force management pharma',
      'distributor fleet app',
      'medicine distributor attendance',
      'outstanding collection app',
      'pharma field tracking GPS',
      'fleet SaaS pan India',
      'salesman tracking software',
    ],
    faq: fleetProduct.faq,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Fleet SaaS', path: FLEET_SAAS_HUB },
    ],
    service: {
      name: 'FastWhistle Fleet Management SaaS',
      description:
        'Best fleet management software for pharmaceutical distributors — manage field employees, tasks, attendance, collections, and accounting integration across India.',
      areaServed: ['Surat', 'Gujarat', 'India'],
      serviceType: 'Fleet management software',
    },
    softwareApplication: true,
    localBusiness: true,
  },
  /** @deprecated use fleetSaas — kept for backward-compatible PageSeo pageKey="fleet" */
  fleet: {
    path: FLEET_SAAS_HUB,
    title: 'Best Pharma Fleet Management Software India | FastWhistle Fleet SaaS',
    description:
      'Pan-India pharma fleet SaaS for medicine distributors — live GPS, attendance, TADA, collections & ZeroMDS sync.',
    keywords: [...baseKeywords, 'fleet management software India'],
    faq: fleetProduct.faq,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Fleet SaaS', path: FLEET_SAAS_HUB },
    ],
    softwareApplication: true,
  },
  compareLogisticsVsFleet: {
    path: '/compare/logistics-vs-fleet-saas',
    title: 'FastWhistle Logistics vs Fleet SaaS | Which Product Fits Your Distributor?',
    description:
      'Compare FastWhistle managed medicine delivery (Ahmedabad, Surat, Pune, Mumbai) vs pan-India Fleet SaaS for your own field team. Feature table, pricing models, and use cases.',
    keywords: [...baseKeywords, 'FastWhistle vs courier', 'pharma logistics vs fleet software', 'managed delivery vs fleet app'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Compare', path: '/compare/logistics-vs-fleet-saas' },
    ],
  },
  contact: {
    path: '/contact-us',
    title: 'Contact FastWhistle | Medicine Delivery & Fleet Service Surat',
    description:
      'Contact FastWhistle for medical delivery service and fleet management in Surat. Visit our Vesu office or 5INCH VENTURES head office. Phone, email, WhatsApp & directions.',
    keywords: [
      ...baseKeywords,
      'FastWhistle contact',
      'FastWhistle Surat office',
      'medicine delivery contact Surat',
      'fleet service contact Gujarat',
      '5INCH VENTURES PRIVATE LIMITED',
    ],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Contact Us', path: '/contact-us' },
    ],
    localBusiness: true,
  },
  careers: {
    path: '/careers',
    title: 'Careers at FastWhistle | Pharma Logistics & Fleet Tech Jobs Surat',
    description:
      'Join FastWhistle in Surat. Open roles in software, mobile development, field operations, sales & customer support for pharma logistics and fleet SaaS products.',
    keywords: [
      ...baseKeywords,
      'FastWhistle careers',
      'pharma tech jobs Surat',
      'logistics jobs Gujarat',
      'fleet software jobs India',
    ],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Careers', path: '/careers' },
    ],
  },
  privacyPolicy: {
    path: '/privacy-policy',
    title: 'Privacy Policy | FastWhistle',
    description: 'FastWhistle privacy policy — how we collect, use, and protect personal information across our logistics and fleet services.',
    keywords: ['FastWhistle privacy policy', 'pharma app privacy'],
    noindex: false,
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Privacy Policy', path: '/privacy-policy' },
    ],
  },
  terms: {
    path: '/terms-condition',
    title: 'Terms & Conditions | FastWhistle',
    description: 'Terms and conditions for using FastWhistle logistics, fleet SaaS, distributor panel, and related services.',
    keywords: ['FastWhistle terms', 'service terms'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Terms & Conditions', path: '/terms-condition' },
    ],
  },
  fleetPrivacy: {
    path: '/fleet-privacy-policy',
    title: 'Fleet App Privacy Policy | FastWhistle',
    description: 'Privacy policy for the FastWhistle Fleet mobile application used by distributor field teams.',
    keywords: ['fleet app privacy', 'FastWhistle fleet privacy'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Fleet Privacy Policy', path: '/fleet-privacy-policy' },
    ],
  },
  distributorPrivacy: {
    path: '/distributor-privacy-policy',
    title: 'Distributor Privacy Policy | FastWhistle',
    description: 'Privacy policy for FastWhistle distributor panel and distributor-facing services.',
    keywords: ['distributor privacy policy', 'FastWhistle distributor'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Distributor Privacy Policy', path: '/distributor-privacy-policy' },
    ],
  },
  retailersPrivacy: {
    path: '/retailers-privacy-policy',
    title: 'Retailers Privacy Policy | FastWhistle',
    description: 'Privacy policy for retailer-facing FastWhistle services and data handling.',
    keywords: ['retailer privacy policy', 'FastWhistle retailers'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Retailers Privacy Policy', path: '/retailers-privacy-policy' },
    ],
  },
  riderPrivacy: {
    path: '/rider-privacy-policy',
    title: 'Rider App Privacy Policy | FastWhistle',
    description: 'Privacy policy for the FastWhistle Rider mobile app including location, camera, and delivery tracking permissions.',
    keywords: ['FastWhistle rider privacy', 'delivery rider app privacy India'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Rider Privacy Policy', path: '/rider-privacy-policy' },
    ],
  },
  chakradharPrivacy: {
    path: '/chakradhar_privacy_policy.html',
    title: 'Chakradhar Privacy Policy | FastWhistle',
    description: 'Privacy policy for the Chakradhar distributor application.',
    keywords: ['Chakradhar privacy policy', 'FastWhistle partner app'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Chakradhar Privacy Policy', path: '/chakradhar_privacy_policy.html' },
    ],
  },
  pavanDistributorPrivacy: {
    path: '/pavan_distributor_privacy_policy.html',
    title: 'Pavan Distributor Privacy Policy | FastWhistle',
    description: 'Privacy policy for the Pavan Distributor mobile application.',
    keywords: ['Pavan Distributor privacy policy', 'FastWhistle partner app'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Pavan Distributor Privacy Policy', path: '/pavan_distributor_privacy_policy.html' },
    ],
  },
  zeromedsPrivacy: {
    path: '/zeromeds_privacy_policy.html',
    title: 'ZeroMeds Privacy Policy | FastWhistle',
    description: 'Privacy policy for the ZeroMeds application and related services.',
    keywords: ['ZeroMeds privacy policy', 'FastWhistle partner app'],
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'ZeroMeds Privacy Policy', path: '/zeromeds_privacy_policy.html' },
    ],
  },
  ...landingPageSeo,
};

/** Canonical sitemap paths (hub-and-spoke; excludes legacy redirects) */
export const SITEMAP_ENTRIES = CANONICAL_SITEMAP_ENTRIES;

export const LEGAL_PATH_SEO = {
  '/privacy-policy': 'privacyPolicy',
  '/terms-condition': 'terms',
  '/fleet-privacy-policy': 'fleetPrivacy',
  '/distributor-privacy-policy': 'distributorPrivacy',
  '/retailers-privacy-policy': 'retailersPrivacy',
  '/rider-privacy-policy': 'riderPrivacy',
  '/chakradhar_privacy_policy.html': 'chakradharPrivacy',
  '/pavan_distributor_privacy_policy.html': 'pavanDistributorPrivacy',
  '/zeromeds_privacy_policy.html': 'zeromedsPrivacy',
};

export function getCanonicalUrl(path = '/') {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${normalized === '/' ? '' : normalized}`;
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Corporation',
    name: SITE_NAME,
    legalName: PARENT_COMPANY,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    email: contact.email,
    telephone: contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: OFFICE.address,
      addressLocality: OFFICE.city,
      addressRegion: OFFICE.state,
      postalCode: OFFICE.postalCode,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: OFFICE.lat,
      longitude: OFFICE.lng,
    },
    sameAs: [
      social.facebook,
      social.instagram,
      social.linkedin,
      social.twitter,
      PLAY_STORE_LINKS.fleet,
      PLAY_STORE_LINKS.distributor,
    ].filter(Boolean),
    description:
      'FastWhistle is an Indian B2B pharmaceutical logistics and fleet management SaaS platform. Managed same-day medicine delivery in Ahmedabad, Surat, Pune, and Mumbai; Fleet SaaS pan-India for distributors.',
    parentOrganization: {
      '@type': 'Organization',
      name: PARENT_COMPANY,
    },
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: PAGE_SEO.home.description,
    publisher: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    inLanguage: 'en-IN',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/medical-delivery-service?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
}

export function buildLocalBusinessSchema(city = 'Surat') {
  return {
    '@context': 'https://schema.org',
    '@type': ['LocalBusiness', 'ProfessionalService'],
    name: `${SITE_NAME} — ${city}`,
    image: DEFAULT_OG_IMAGE,
    url: SITE_URL,
    telephone: contact.phone,
    email: contact.email,
    priceRange: '₹₹',
    address: {
      '@type': 'PostalAddress',
      streetAddress: OFFICE.address,
      addressLocality: OFFICE.city,
      addressRegion: OFFICE.state,
      postalCode: OFFICE.postalCode,
      addressCountry: 'IN',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: OFFICE.lat,
      longitude: OFFICE.lng,
    },
    areaServed: [
      { '@type': 'City', name: 'Surat' },
      { '@type': 'City', name: 'Ahmedabad' },
      { '@type': 'State', name: 'Gujarat' },
      { '@type': 'Country', name: 'India' },
    ],
    knowsAbout: [
      'Medicine delivery service',
      'Medical delivery service',
      'Pharmaceutical logistics',
      'Fleet management software',
      'Pharma distributor software',
    ],
    description:
      'Medical delivery service and fleet management for pharmaceutical distributors in Gujarat and pan-India.',
  };
}

export function buildSoftwareApplicationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'FastWhistle Fleet SaaS',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Android, iOS, Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
      description: 'Contact for fleet SaaS pricing',
    },
    provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    description:
      'Fleet management software for medicine distributors — live GPS tracking, attendance, tasks, collections, and accounting sync.',
    featureList: [
      'Live GPS fleet tracking',
      'Attendance and leave management',
      'Order and collection tasks',
      'Outstanding collection tracking',
      'Accounting software sync',
    ],
    areaServed: { '@type': 'Country', name: 'India' },
  };
}

export function buildFaqSchema(faqItems = []) {
  if (!faqItems?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function buildBreadcrumbSchema(breadcrumbs = []) {
  if (!breadcrumbs?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: getCanonicalUrl(crumb.path),
    })),
  };
}

export function buildServiceSchema(service) {
  if (!service) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.name,
    description: service.description,
    provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
    areaServed: service.areaServed.map((name) => ({ '@type': 'Place', name })),
    serviceType: service.serviceType || 'Pharmaceutical logistics and fleet management software',
  };
}

export function buildWebPageSchema({ title, description, path }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description,
    url: getCanonicalUrl(path),
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
    inLanguage: 'en-IN',
  };
}

export function getSchemasForPage(pageKey, { includeOrg = false, includeLocalBusiness = false } = {}) {
  const page = PAGE_SEO[pageKey];
  if (!page) return [];

  const schemas = [buildWebPageSchema(page), buildBreadcrumbSchema(page.breadcrumbs)];

  if (page.faq) {
    const faq = buildFaqSchema(page.faq);
    if (faq) schemas.push(faq);
  }

  if (page.service) {
    const service = buildServiceSchema(page.service);
    if (service) schemas.push(service);
  }

  if (page.softwareApplication) {
    schemas.push(buildSoftwareApplicationSchema());
  }

  if (includeOrg) {
    schemas.push(buildOrganizationSchema(), buildWebSiteSchema());
  }

  if (includeLocalBusiness || page.localBusiness) {
    schemas.push(buildLocalBusinessSchema(page.city || 'Surat'));
  }

  return schemas.filter(Boolean);
}
